/**
 * PM Tasks Table Component
 * Displays PM Calendar tasks in table format similar to alerts
 */

import React, { useState, useMemo } from 'react';
import { Clock, MapPin, Users, AlertCircle, Activity, CalendarX } from 'lucide-react';
import { PMTask } from '../hooks/usePMTasks';

interface PMTasksTableProps {
  tasks: PMTask[];
  loading: boolean;
  error: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  scheduled: '#0084CF',
  'in-progress': '#f59e0b',
  completed: '#10b981',
  overdue: '#ef4444',
  cancelled: '#6b7280'
};

export const PMTasksTable: React.FC<PMTasksTableProps> = ({ tasks, loading, error }) => {
  if (error) {
    return (
      <div className="pm-tasks-table-error">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pm-tasks-table-loading">
        <div className="spinner"></div>
        Loading PM Calendar tasks...
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-xl border border-slate-200/60 shadow-sm">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100 shadow-sm">
          <CalendarX size={32} className="text-slate-400" />
        </div>
        <h3 className="text-slate-800 font-semibold text-lg mb-1">No Activities Scheduled</h3>
        <p className="text-slate-500 text-sm text-center max-w-sm">
          There are currently no PM Calendar activities found for the selected time period or site.
        </p>
      </div>
    );
  }

  const pmStats = {
    total: tasks.length,
    scheduled: tasks.filter(t => t.status === 'scheduled').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [equipmentFilter, setEquipmentFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const uniquePriorities = useMemo(() => Array.from(new Set(tasks.map(t => t.priority))), [tasks]);
  const uniqueEquipments = useMemo(() => Array.from(new Set(tasks.map(t => t.equipmentName).filter(Boolean))), [tasks]);
  const uniqueTypes = useMemo(() => Array.from(new Set(tasks.map(t => t.type))), [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchStatus = statusFilter === 'all' || task.status === statusFilter;
      const matchPriority = priorityFilter === 'all' || task.priority === priorityFilter;
      const matchEquipment = equipmentFilter === 'all' || task.equipmentName === equipmentFilter;
      const matchType = typeFilter === 'all' || task.type === typeFilter;
      return matchStatus && matchPriority && matchEquipment && matchType;
    });
  }, [tasks, statusFilter, priorityFilter, equipmentFilter, typeFilter]);

  return (
    <div className="pm-tasks-wrapper">
      {/* PM Tasks Summary Section */}
      <div className="pm-tasks-section-compact mb-4">
        <div className="section-header">
          <Activity size={14} />
          <span>PM Calendar Activities</span>
        </div>
        <div className="pm-tasks-compact-grid">
          <div 
            className={`pm-task-stat ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
            title="Show all tasks"
          >
            <div className="stat-value">{pmStats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div 
            className={`pm-task-stat ${statusFilter === 'scheduled' ? 'active' : ''}`}
            onClick={() => setStatusFilter('scheduled')}
            title="Filter by Scheduled"
          >
            <div className="stat-value">{pmStats.scheduled}</div>
            <div className="stat-label">Scheduled</div>
          </div>
          <div 
            className={`pm-task-stat ${statusFilter === 'in-progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter('in-progress')}
            title="Filter by In Progress"
          >
            <div className="stat-value">{pmStats.inProgress}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div 
            className={`pm-task-stat ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed')}
            title="Filter by Completed"
          >
            <div className="stat-value">{pmStats.completed}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      <div className="pm-tasks-filters">
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="pm-filter-select">
          <option value="all">All Priorities</option>
          {uniquePriorities.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="pm-filter-select">
          <option value="all">All Types</option>
          {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={equipmentFilter} onChange={e => setEquipmentFilter(e.target.value)} className="pm-filter-select">
          <option value="all">All Equipment</option>
          {uniqueEquipments.map(e => <option key={e as string} value={e as string}>{e}</option>)}
        </select>
        {(priorityFilter !== 'all' || equipmentFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all') && (
          <button onClick={() => { setPriorityFilter('all'); setTypeFilter('all'); setEquipmentFilter('all'); setStatusFilter('all'); }} className="pm-filter-reset">
            Clear Filters
          </button>
        )}
      </div>

      <div className="pm-tasks-table-container">
        <table className="pm-tasks-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Task Title</th>
            <th>Type</th>
            <th>Equipment</th>
            <th>Location</th>
            <th>Assigned To</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {filteredTasks.length > 0 ? (
            filteredTasks.map((task) => (
              <tr key={task.id} className="pm-task-row">
              <td className="date-cell">
                <span className="date-value">{task.date}</span>
              </td>
              <td className="time-cell">
                <div className="time-badge">
                  <Clock size={12} />
                  <span>{task.startTime}</span>
                </div>
              </td>
              <td className="title-cell">
                <div className="title-content">
                  <span className="title">{task.title}</span>
                  {task.isRecurring && (
                    <span className="recurring-tag">Recurring</span>
                  )}
                </div>
              </td>
              <td className="type-cell">
                <span 
                  className="type-badge" 
                  style={{ 
                    backgroundColor: task.color + '20', 
                    color: task.color,
                    borderColor: task.color + '40'
                  }}
                >
                  {task.type}
                </span>
              </td>
              <td className="equipment-cell">
                {task.equipmentName ? (
                  <span className="equipment-badge">{task.equipmentName}</span>
                ) : (
                  <span className="empty-cell">—</span>
                )}
              </td>
              <td className="location-cell">
                {task.location ? (
                  <div className="location-badge">
                    <MapPin size={12} />
                    <span>{task.location}</span>
                  </div>
                ) : (
                  <span className="empty-cell">—</span>
                )}
              </td>
              <td className="assigned-cell">
                {task.assignedTo ? (
                  <div className="assigned-badge">
                    <Users size={12} />
                    <span>{task.assignedTo}</span>
                  </div>
                ) : (
                  <span className="empty-cell">Unassigned</span>
                )}
              </td>
              <td className="status-cell">
                <span 
                  className="status-badge"
                  style={{ backgroundColor: STATUS_COLORS[task.status] + '20', color: STATUS_COLORS[task.status] }}
                >
                  {task.status.replace('-', ' ')}
                </span>
              </td>
              <td className="priority-cell">
                <span 
                  className={`priority-badge priority-${task.priority}`}
                >
                  {task.priority}
                </span>
              </td>
              <td className="description-cell">
                <span className="description" title={task.description}>
                  {task.description ? task.description.substring(0, 30) + (task.description.length > 30 ? '...' : '') : '—'}
                </span>
              </td>
            </tr>
            ))
          ) : (
            <tr>
              <td colSpan={10} className="empty-results-cell">
                <div className="no-results-state">
                  <AlertCircle size={24} className="text-gray-400 mb-2" />
                  <p>No tasks match the selected filters.</p>
                  <button onClick={() => { setPriorityFilter('all'); setTypeFilter('all'); setEquipmentFilter('all'); setStatusFilter('all'); }}>Clear Filters</button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .pm-tasks-wrapper {
          display: flex;
          flex-direction: column;
        }

        .pm-tasks-section-compact {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .section-header svg {
          color: #0f172a;
        }

        .pm-tasks-compact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
          gap: 8px;
        }

        .pm-task-stat {
          padding: 12px 8px;
          border-radius: 6px;
          text-align: center;
          border: 1px solid #8AC4E6;
          background: linear-gradient(135deg, #E3F0F9 0%, #f5f3ff 100%);
          transition: all 0.2s ease;
          cursor: pointer;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .pm-task-stat:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .pm-task-stat.active {
          border-color: #0084CF;
          background: linear-gradient(135deg, #E3F0F9 0%, #F2F8FC 100%);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
        }

        .pm-tasks-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
          align-items: center;
        }

        .pm-filter-select {
          padding: 8px 12px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 13px;
          color: #1e293b;
          background-color: white;
          outline: none;
          min-width: 140px;
          cursor: pointer;
        }

        .pm-filter-select:focus {
          border-color: #0084CF;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .pm-filter-reset {
          padding: 8px 12px;
          color: #ef4444;
          background: transparent;
          border: none;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .pm-filter-reset:hover {
          text-decoration: underline;
        }

        .empty-results-cell {
          text-align: center;
          padding: 40px !important;
        }

        .no-results-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .no-results-state button {
          margin-top: 12px;
          padding: 6px 12px;
          background-color: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          color: #334155;
          cursor: pointer;
          font-size: 13px;
        }
        
        .no-results-state button:hover {
          background-color: #e2e8f0;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2px;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .stat-label {
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .mb-4 {
          margin-bottom: 1rem;
        }

        .pm-tasks-table-container {
          width: 100%;
          overflow-x: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: white;
        }

        .pm-tasks-table {
          width: 100%;
          border-collapse: collapse;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 13px;
        }

        .pm-tasks-table thead {
          background: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .pm-tasks-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #475569;
          white-space: nowrap;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
        }

        .pm-tasks-table tbody tr {
          border-bottom: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .pm-tasks-table tbody tr:hover {
          background: #f8fafc;
        }

        .pm-task-row td {
          padding: 12px 16px;
          color: #334155;
          vertical-align: middle;
        }

        .date-cell {
          font-weight: 600;
          color: #0f172a;
        }

        .date-value {
          display: inline-block;
          background: #E3F0F9;
          color: #0066A3;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .time-cell {
          width: 80px;
        }

        .time-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          width: fit-content;
          color: #64748b;
        }

        .title-cell {
          max-width: 200px;
        }

        .title-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .title {
          font-weight: 600;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .recurring-tag {
          display: inline-block;
          background: #C2DFF1;
          color: #0066A3;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 600;
          white-space: nowrap;
        }

        .type-cell {
          width: 90px;
        }

        .type-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid;
          white-space: nowrap;
        }

        .equipment-cell {
          width: 110px;
        }

        .equipment-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          background: #f0fdf4;
          color: #15803d;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid #bbf7d0;
          white-space: nowrap;
        }

        .location-cell {
          width: 120px;
        }

        .location-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          color: #64748b;
          width: fit-content;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .assigned-cell {
          width: 120px;
        }

        .assigned-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          color: #64748b;
          width: fit-content;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .status-cell {
          width: 100px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid;
          white-space: nowrap;
          text-transform: capitalize;
        }

        .priority-cell {
          width: 80px;
        }

        .priority-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid;
          white-space: nowrap;
          text-transform: uppercase;
        }

        .priority-low {
          background: #E3F0F9;
          color: #0066A3;
          border-color: #8AC4E6;
        }

        .priority-medium {
          background: #fef3c7;
          color: #92400e;
          border-color: #fde047;
        }

        .priority-high {
          background: #fee2e2;
          color: #991b1b;
          border-color: #fca5a5;
        }

        .priority-critical {
          background: #7c2d12;
          color: #ffedd5;
          border-color: #ea580c;
        }

        .description-cell {
          max-width: 200px;
        }

        .description {
          color: #64748b;
          font-style: italic;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .empty-cell {
          color: #cbd5e1;
          font-style: italic;
        }

        .pm-tasks-table-error,
        .pm-tasks-table-loading,
        .pm-tasks-table-empty {
          padding: 32px;
          text-align: center;
          color: #64748b;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .pm-tasks-table-error {
          color: #dc2626;
          background: #fef2f2;
          border-color: #fecaca;
        }

        .pm-tasks-table-empty svg {
          color: #94a3b8;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #e2e8f0;
          border-top-color: #0084CF;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}} />
    </div>
  );
};
